import type { MappingResponse } from '../../services/api';
import type {
  PipelineModel,
  PipelineSourceTable,
  PipelineJoinNode,
  PipelineTargetTable,
  PipelineStep,
  PipelineEdge,
} from './types';

/**
 * Converts the TransformationPlan into a structured PipelineModel
 * with actual lineage edges derived from step inputs.
 */
export function buildPipelineModel(
  mappingResponse?: MappingResponse | null,
): PipelineModel {
  const empty: PipelineModel = { sources: [], joins: [], targets: [], edges: [] };
  if (!mappingResponse?.transformation_plan) return empty;

  const plan = mappingResponse.transformation_plan;

  // ── 1. Sources ──────────────────────────────────────────
  const sources: PipelineSourceTable[] = (plan.dependent_sources ?? []).map((alias) => ({
    alias,
    columnCount: undefined,
  }));
  const sourceSet = new Set(sources.map((s) => s.alias));

  // ── 2. Joins ────────────────────────────────────────────
  const joins: PipelineJoinNode[] = [];
  if (plan.join_tree?.nodes) {
    Object.entries(plan.join_tree.nodes).forEach(([joinId, jn]) => {
      const leftAlias = jn.inputs?.[0]?.source_alias ?? '';
      const rightAlias = jn.inputs?.[1]?.source_alias ?? '';
      const condParts = (jn.conditions ?? []).map((c) => {
        const lCol = (c.left ?? '').split('.').pop() ?? '';
        const rCol = (c.right ?? '').split('.').pop() ?? '';
        return `${lCol} = ${rCol}`;
      });
      joins.push({
        id: joinId,
        joinType: jn.join_type ?? 'left',
        leftTable: leftAlias,
        rightTable: rightAlias,
        condition: condParts.join(', ') || '(no condition)',
      });
    });
  }

  // ── 3. Targets + Edges ──────────────────────────────────
  const edges: PipelineEdge[] = [];
  const targets: PipelineTargetTable[] = (plan.targets ?? []).map((tgt) => {
    const steps: PipelineStep[] = (tgt.steps ?? []).map((s) => ({
      stepId: s.step_id,
      operation: s.operation ?? 'unknown',
      description: s.description ?? '',
      inputs: s.inputs ?? [],
      outputs: s.outputs ?? [],
      expression: s.parameters?.expression as string | undefined,
      confidence: s.confidence ?? 0,
    }));

    // Derive which source tables feed this target from step inputs
    const srcTablesForTarget = new Set<string>();
    steps.forEach((s) => {
      s.inputs.forEach((inp) => {
        const tbl = inp.split('.')[0];
        if (tbl && sourceSet.has(tbl)) {
          srcTablesForTarget.add(tbl);
        }
      });
    });

    // Create edges
    srcTablesForTarget.forEach((src) => {
      edges.push({ from: src, to: tgt.target_table });
    });

    const directCount = steps.filter((s) => s.operation === 'direct').length;
    const expressionCount = steps.filter((s) => s.operation === 'expression').length;
    const avgConfidence =
      steps.length > 0
        ? steps.reduce((sum, s) => sum + s.confidence, 0) / steps.length
        : 0;

    return {
      name: tgt.target_table,
      description: tgt.description ?? '',
      stepCount: steps.length,
      directCount,
      expressionCount,
      steps,
      avgConfidence,
      sourceTables: Array.from(srcTablesForTarget),
    };
  });

  // When mapping completes with no source coverage, the plan may be
  // empty but schema snapshots still declare the tables.
  if (sources.length === 0 || targets.length === 0) {
    const meta = mappingResponse.metadata;
    if (sources.length === 0) {
      const sourceTables = meta?.source_schema_set_snapshot?.tables;
      if (sourceTables) {
        Object.keys(sourceTables).forEach((alias) => {
          sources.push({ alias, columnCount: undefined });
        });
      } else {
        const signature = meta?.source_schema_signature;
        if (signature?.sources && Array.isArray(signature.sources)) {
          signature.sources.forEach((src) => {
            const alias = (src.source ?? '').trim();
            if (alias) sources.push({ alias, columnCount: undefined });
          });
        }
      }
      mappingResponse.dependent_sources?.forEach((alias) => {
        const name = alias?.trim();
        if (name && !sources.some((s) => s.alias === name)) {
          sources.push({ alias: name, columnCount: undefined });
        }
      });
    }
    const targetTables =
      plan.target_schema_snapshot?.tables
      ?? meta?.target_schema_snapshot?.tables
      ?? meta?.schema_context?.target_schema_snapshot?.tables;
    if (targets.length === 0 && targetTables) {
      Object.keys(targetTables).forEach((name) => {
        targets.push({
          name,
          description: name,
          stepCount: 0,
          directCount: 0,
          expressionCount: 0,
          steps: [],
          avgConfidence: 0,
          sourceTables: [],
        });
      });
    }
  }

  return { sources, joins, targets, edges };
}
